/**
 * Slash Commands
 *
 * Queries the unified plugin registry for slash command definitions.
 * Provides matching, parsing, and execution for `/command` shortcuts
 * that execute tools directly, bypassing the AI orchestrator.
 */

import { executeTool, getToolDefinition, type ToolContext } from './tools/tool-registry';
import { getSlashCommands as getRegistrySlashCommands } from '../plugins/registry';
import type { ResolvedSlashCommand } from '../plugins/types';
import { isToolOffered } from './kits';

// ── Public Type ─────────────────────────────────────────────
// Re-export as SlashCommand for backward compat with ChatPanel.

export type SlashCommand = ResolvedSlashCommand;

// ── Lookup ──────────────────────────────────────────────────

/**
 * The commands of the tools the session offers, read each time: a hot update
 * re-registers its tools, and a kit switched off takes its commands with it.
 */
function ensureLoaded(): { commands: SlashCommand[]; commandMap: Map<string, SlashCommand> } {
    const commands = getRegistrySlashCommands().filter(command => isToolOffered(command.toolName));
    return { commands, commandMap: new Map(commands.map(c => [c.name, c])) };
}

// ── Queries ─────────────────────────────────────────────────

/** Get all registered slash commands. */
export function getSlashCommands(): readonly SlashCommand[] {
    return ensureLoaded().commands;
}

/** Get commands matching a partial name (for suggestions). */
export function matchSlashCommands(partial: string): SlashCommand[] {
    const { commands } = ensureLoaded();
    const lower = partial.toLowerCase();
    return commands.filter(c => c.name.startsWith(lower));
}

// ── Parsing ─────────────────────────────────────────────────

/**
 * Parse a slash command string like "/image a red car" into
 * { command, rawArgs } or null if not a valid slash command.
 */
export function parseSlashInput(text: string): { command: SlashCommand; rawArgs: string } | null {
    const trimmed = text.trim();
    if (!trimmed.startsWith('/')) return null;

    const spaceIdx = trimmed.indexOf(' ');
    const name = spaceIdx === -1
        ? trimmed.slice(1)
        : trimmed.slice(1, spaceIdx);
    const rawArgs = spaceIdx === -1 ? '' : trimmed.slice(spaceIdx + 1).trim();

    const { commandMap } = ensureLoaded();
    const command = commandMap.get(name);
    if (!command) return null;

    return { command, rawArgs };
}

// ── Execution ───────────────────────────────────────────────

/**
 * Execute a slash command directly, bypassing the AI orchestrator.
 * Returns the tool result string. A tool whose code a specialist writes
 * (build_spatial, build_mesh) is not run here: the chat panel sends it as a
 * turn, and the gateway writes the code.
 */
export async function executeSlashCommand(
    command: SlashCommand,
    rawArgs: string,
    ctx: ToolContext,
): Promise<string> {
    const selectedName = ctx.selectedObject?.name;
    const input = command.parseArgs(rawArgs, selectedName);
    if (!getToolDefinition(command.toolName)) {
        return `Error: Tool "${command.toolName}" not found.`;
    }
    return executeTool(command.toolName, input, ctx);
}
